import { useState, useEffect } from 'react';
import { AppRoute } from '@/App';
import { useNavigate } from 'react-router-dom';
import { CardOfTheDayDomain } from '@/types/Card';
import cardOfThedayRepository from '@/services/cardOfTheDayRepository';
import { getMillisecondsUntilReset } from '@/utils/timerUtils';
import { formatTimeToNext } from '@/screens/HomeScreen/utils/timeUtils';
import { selectCardOfTheDayLog } from '@/firebase/events';
import { useSubscription } from '@/contexts/SubscriptionContext';

export interface CardOfTheDayScreenViewModel {
  selectedCard: CardOfTheDayDomain | null;
  isLoading: boolean;
  error: string | null;
  timeToNextCard: string;
  selectCard: (index: number) => Promise<void>;
  navigateToHome: () => void; 
}

export const useCardOfTheDayScreenViewModel = (): CardOfTheDayScreenViewModel => {
  const [selectedCard, setSelectedCard] = useState<CardOfTheDayDomain | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeToNextCard, setTimeToNextCard] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getCardOfTheDay, selectCardOfTheDay } = cardOfThedayRepository;
  const { subscription, isLoading: subscriptionLoading } = useSubscription();

  const updateScreenState = async () => {
    if (subscriptionLoading) {
      return;
    }
    setIsLoading(true);
    setError(null);
      try {
        await updateSelectedCardOfTheDay();
      } catch (err) {
        setError("Error updating screen state");
      } finally {
        setIsLoading(false);
      }
  };

  const updateSelectedCardOfTheDay = async () => {
    const cardOfTheDay = await getCardOfTheDay();
    if (cardOfTheDay) {
      setSelectedCard(cardOfTheDay);
    }
  };

  const countTimeToNextCard = () => {
    const resetDate = selectedCard?.resetDate ?? null;
    if (!resetDate) return;

    const diff = getMillisecondsUntilReset(resetDate);
    setTimeToNextCard(formatTimeToNext(diff, "Открытие через"));
  };

  const navigateToHome = () => {
    navigate(AppRoute.Home);
  };

  const selectCard = async (index: number) => {
    try {
      await selectCardOfTheDay(index);
      await selectCardOfTheDayLog(subscription?.isSubscribed || false);
      await updateScreenState();
    } catch (error) {
      console.error("Error selecting card", error);
    }
  };

  useEffect(() => {
    countTimeToNextCard();
    if (!selectedCard) {
      updateScreenState();
    }
  }, [selectedCard, subscriptionLoading]);

  return {
    selectedCard,
    isLoading,
    error,
    navigateToHome,
    selectCard, 
    timeToNextCard,
  };
}; 
